import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Rocket, Zap, Crown, Sparkles, Info } from 'lucide-react';
import PaymentModal from '../components/PaymentModal';
import TrialModal from '../components/TrialModal';
import { RECOMMENDED_START_SOL } from '../lib/site';

const SOL_USD = 150;

const STARTER_FEATURES = [
  'Up to 100 snipes per day',
  'Rugpull shield v1 (99.0% detection)',
  '1 active wallet',
  'Dashboard + real-time token feed',
  'Community support (email, <48h)',
  'Pump.fun only — Raydium comes in Pro',
];
const PRO_FEATURES = [
  'Unlimited snipes, unlimited tokens scanned',
  'Rugpull shield v3 (99.2%) + honeypot detector',
  '5 active wallets, auto-rotation',
  'Copy-trading — follow up to 50 top snipers',
  'AI agent mode (Gemini 3.1 / GPT-5.4 / Claude 4.7 / Grok 4.20)',
  'Priority Telegram support (<1h)',
  'Custom RPC endpoints + MEV protection',
  'REST API + WebSockets — 10k req/day',
  'Source code access (private GitHub)',
];
const WHALE_FEATURES = [
  'Everything in Pro',
  'Unlimited wallets + institutional key-mgmt',
  'Unlimited copy-trading slots',
  'Dedicated bare-metal RPC in your region',
  'Private Telegram channel with founders',
  'REST + WS API — unlimited, no rate-limit',
  '1-on-1 strategy onboarding (2h Zoom)',
  'Early access to v4 features (Base, Blast, EVM)',
  'White-label licensing on request',
];

const PLANS = {
  monthly: [
    {
      key: 'starter',
      name: 'Starter',
      price: 0.5, priceUsd: 0.5 * SOL_USD,
      period: 'mo',
      Icon: Zap,
      color: 'text-sky-400',
      desc: 'Try the bot. Good for curious traders with a first bag.',
      features: STARTER_FEATURES,
      cta: 'Start free trial',
      flow: 'trial',
    },
    {
      key: 'pro',
      name: 'Pro',
      price: 1.5, priceUsd: 1.5 * SOL_USD,
      period: 'mo',
      Icon: Rocket,
      color: 'text-[hsl(var(--primary))]',
      desc: 'For serious snipers. Most popular plan — picks itself.',
      features: PRO_FEATURES,
      popular: true,
      cta: 'Go Pro',
      flow: 'pay',
    },
    {
      key: 'whale',
      name: 'Whale',
      price: 4.9, priceUsd: 4.9 * SOL_USD,
      period: 'mo',
      Icon: Crown,
      color: 'text-amber-400',
      desc: 'For funds, OTC desks and power users. White-glove service.',
      features: WHALE_FEATURES,
      cta: 'Go Whale',
      flow: 'pay',
    },
  ],
  yearly: [
    { key: 'starter', name: 'Starter', price: 5, priceUsd: 5 * SOL_USD, period: 'yr', Icon: Zap, color: 'text-sky-400', desc: 'Try the bot. Save 17% vs monthly.', features: STARTER_FEATURES, cta: 'Start free trial', flow: 'trial' },
    { key: 'pro', name: 'Pro', price: 14, priceUsd: 14 * SOL_USD, period: 'yr', Icon: Rocket, color: 'text-[hsl(var(--primary))]', desc: 'Save 22% vs monthly. Most popular.', features: PRO_FEATURES, popular: true, cta: 'Go Pro', flow: 'pay' },
    { key: 'whale', name: 'Whale', price: 49, priceUsd: 49 * SOL_USD, period: 'yr', Icon: Crown, color: 'text-amber-400', desc: 'Save 17% vs monthly. White-glove.', features: WHALE_FEATURES, cta: 'Go Whale', flow: 'pay' },
  ],
};

export default function Pricing() {
  const [period, setPeriod] = useState('monthly');
  const [payPlan, setPayPlan] = useState(null);
  const [trialPlan, setTrialPlan] = useState(null);

  const priceLabel = (p) => `${p.price} SOL / ${p.period} (~$${Math.round(p.priceUsd).toLocaleString()})`;

  const openForPlan = (p) => {
    const planWithLabel = { ...p, priceLabel: priceLabel(p) };
    if (p.flow === 'trial') setTrialPlan(planWithLabel);
    else setPayPlan(planWithLabel);
  };

  return (
    <div className="pt-12 sm:pt-16 pb-16 sm:pb-20" data-testid="pricing-page">
      <section className="container-x py-12 text-center">
        <span className="chip"><Sparkles className="w-3 h-3" /> Pricing</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          Simple pricing, <span className="gradient-text">outsized returns</span>
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
          Pay in SOL. Cancel anytime. All plans include audited smart vaults, non-custodial signing and 24/7 monitoring.
        </p>
        <div className="inline-flex items-center gap-1 p-1 mt-8 rounded-xl border border-border bg-background/40 backdrop-blur-xl">
          {['monthly', 'yearly'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`pricing-toggle-${p}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                period === p ? 'bg-[hsl(var(--primary))] text-black' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p === 'monthly' ? 'Monthly' : 'Yearly'}
              {p === 'yearly' && <span className="ml-2 text-[10px] text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 px-1.5 py-0.5 rounded font-mono">-22%</span>}
            </button>
          ))}
        </div>
      </section>

      <section className="container-x grid md:grid-cols-3 gap-6">
        {PLANS[period].map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className={`relative glass-card p-5 sm:p-7 ${p.popular ? 'border-[hsl(var(--primary))]/60 neon-border' : ''}`}
            data-testid={`plan-card-${p.key}`}
          >
            {p.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip !bg-[hsl(var(--primary))] !text-black !border-transparent font-bold">
                MOST POPULAR
              </div>
            )}
            <div className="flex items-center gap-2">
              <p.Icon className={`w-5 h-5 ${p.color}`} />
              <h3 className="font-display font-bold text-lg">{p.name}</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.desc}</p>
            <div className="mt-6 mb-5">
              <span className="font-display font-black text-5xl tracking-tighter">{p.price}</span>
              <span className="text-muted-foreground ml-2">SOL / {p.period}</span>
              <div className="text-xs font-mono text-muted-foreground mt-1">~${Math.round(p.priceUsd).toLocaleString()} USD at spot</div>
            </div>
            <button
              onClick={() => openForPlan(p)}
              className={p.popular ? 'btn-primary w-full justify-center' : 'btn-secondary w-full justify-center'}
              data-testid={`plan-cta-${p.key}`}
            >
              {p.cta}
            </button>
            <ul className="mt-7 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </section>

      <section className="container-x mt-10">
        <div className="glass-card p-5 sm:p-6 flex items-start gap-4" data-testid="pricing-funding-tip">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
            <Info className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-bold text-base">Recommended starting bankroll</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              To give the bot enough rounds to find winners while surviving a cold streak, we recommend funding your sniping wallet with at least <span className="text-[hsl(var(--primary))] font-semibold">{RECOMMENDED_START_SOL} SOL</span> (~${(RECOMMENDED_START_SOL * SOL_USD).toLocaleString()}). This is not part of the subscription — it stays in your wallet.
            </p>
          </div>
        </div>
      </section>

      <section className="container-x mt-10">
        <h3 className="font-display font-bold text-lg mb-4">Compare plans</h3>
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm font-mono min-w-[540px]">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-3 px-4 font-normal">Feature</th>
                <th className="py-3 px-4 font-normal text-sky-400">Starter</th>
                <th className="py-3 px-4 font-normal text-[hsl(var(--primary))]">Pro</th>
                <th className="py-3 px-4 font-normal text-amber-400">Whale</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Daily snipes', '100', 'Unlimited', 'Unlimited'],
                ['Active wallets', '1', '5', 'Unlimited'],
                ['Rugpull shield', 'v1 (99.0%)', 'v3 (99.2%)', 'v3 (99.2%)'],
                ['Copy-trading', '—', '50 slots', 'Unlimited'],
                ['AI agent mode', '—', 'Gemini/GPT/Claude/Grok', 'Gemini/GPT/Claude/Grok'],
                ['Dedicated RPC', '—', 'Shared', 'Bare-metal'],
                ['API access', '—', '10k req/day', 'Unlimited'],
                ['Source code', '—', 'Read-only', 'Read-only'],
                ['Support SLA', '48h email', '1h Telegram', '15m Telegram'],
              ].map((row, i) => (
                <tr key={i} className="border-b border-border/60 last:border-0 text-foreground/90">
                  <td className="py-3 px-4">{row[0]}</td>
                  <td className="py-3 px-4">{row[1]}</td>
                  <td className="py-3 px-4 text-[hsl(var(--primary))]">{row[2]}</td>
                  <td className="py-3 px-4 text-amber-400">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="container-x mt-16">
        <div className="glass-card p-6 sm:p-8 text-center">
          <h3 className="font-display font-bold text-xl">Need something custom?</h3>
          <p className="text-muted-foreground mt-2">We offer white-label solutions, OTC licensing and enterprise SLAs.</p>
          <a href="#/contact" className="btn-secondary mt-5">Talk to sales</a>
        </div>
      </section>

      <PaymentModal open={!!payPlan} plan={payPlan} onClose={() => setPayPlan(null)} />
      <TrialModal
        open={!!trialPlan}
        plan={trialPlan}
        onClose={() => setTrialPlan(null)}
        onPayNow={() => {
          const p = trialPlan;
          setTrialPlan(null);
          setPayPlan(p);
        }}
      />
    </div>
  );
}
